import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import moment from "moment";

import DefaultTextarea from "../inputs/defaultTextarea";
import DefaultSearchSelectInput from "../inputs/defaultSearchSelectInput";
import DefaultDatePicker from "../inputs/defaultDatePicker";
import DefaultInput from "../inputs/defaultInput";
import DefaultButton from "../buttons/defaultButton";
import ReduxFileUploader from "../inputs/reduxFileUploader";
import { RadioInput } from "../inputs/radioInput";
import DefaultCheckbox from "../inputs/defaultCheckbox";
import DynamicSearchSelectInput from "../inputs/dynamicSearchSelectInput";
import { FiEdit2 } from "react-icons/fi";

// Initial values setup
const buildInitialValues = (fields, initialData = {}) => {
  const values = {};
  fields.forEach((field) => {
    let defaultValue = "";

    if (field.type === "number") {
      defaultValue = "";
    } else if (field.type === "checkbox") {
      defaultValue = false;
    } else if (field.type === "select" && (field.multiple || field.isMulti)) {
      defaultValue = [];
    } else if (field.type === "file") {
      defaultValue = [];
    } else if (field.type === "dynamicSearchSelect") {
      defaultValue = []; // because it returns array of selected values
    }

    values[field.name] =
      initialData[field.name] ?? field.initialValue ?? defaultValue;

    if (field.type === "contact") {
      const codeKey = field.countryCodeName || "countryCode";
      const defaultCode = field.initialCountryCode ?? "+91";
      values[codeKey] = initialData[codeKey] ?? defaultCode;
    }
  });
  return values;
};

// Validation schema setup
const buildValidationSchema = (fields, existingEmails = []) => {
  const schema = {};
  fields.forEach((field) => {
    const {
      name,
      type,
      required,
      min,
      max,
      minLength,
      maxLength,
      pattern,
      customValidation,
    } = field;

    let rule;

    switch (type) {
      case "number":
        rule = Yup.mixed() // Use mixed instead of number
          .test("is-number", `${field.label} must be a number`, (value) => {
            if (value === undefined || value === null || value === "")
              return true; // allow empty
            return !isNaN(value); // only validate if there is a value
          });

        if (required) {
          rule = Yup.number()
            .typeError(`${field.label} must be a number`)
            .required(`${field.label} is required`);
        } else {
          // optional: apply min/max only if value exists
          if (min !== undefined || max !== undefined) {
            rule = rule.test(
              "min-max",
              `${field.label} must be between ${min ?? "-∞"} and ${max ?? "∞"}`,
              (value) => {
                if (value === undefined || value === null || value === "")
                  return true;
                if (min !== undefined && value < min) return false;
                if (max !== undefined && value > max) return false;
                return true;
              }
            );
          }
        }

        break;

      case "email":
        rule = Yup.string()
          .email("Invalid email format")
          .test(
            "unique-email",
            "This email is already taken",
            function (value) {
              if (!value) return true; // allow empty if not required
              return !existingEmails.includes(value.toLowerCase());
            }
          );
        break;
      case "url":
        rule = Yup.string().url("Invalid URL format");
        break;
      case "checkbox":
        rule = Yup.boolean();
        break;
      case "select":
        rule = field.multiple ? Yup.array() : Yup.string();
        break;
      case "date":
        rule = Yup.string().test(
          "is-valid-date",
          "Invalid date format",
          function (value) {
            // Allow empty if not required (will be checked by required() later)
            if (!value || value === "") return true;
            return moment(value, "YYYY-MM-DD", true).isValid();
          }
        );
        break;

      case "contact":
        rule = Yup.string().matches(
          /^[0-9]{10}$/,
          "Phone number must be 10 digits"
        );
        break;

      case "file":
        rule = Yup.array();
        break;
      default:
        rule = Yup.string();
    }

    if (required) {
      if (type === "checkbox") {
        rule = rule.oneOf([true], `${field.label} is required`);
      } else if (type === "select" && field.multiple) {
        rule = rule.min(1, `${field.label} is required`);
      } else if (type === "file") {
        rule = rule.min(1, `${field.label} is required`);
      } else {
        rule = rule.required(`${field.label} is required`);
      }
    }

    if (required) {
      if (min !== undefined)
        rule = rule.min(min, `${field.label} must be at least ${min}`);
      if (max !== undefined)
        rule = rule.max(max, `${field.label} must be at most ${max}`);

      if (name === "max_amount") {
        rule = rule.test(
          "max-crore",
          `${field.label} cannot exceed 1 crore (₹1,00,00,000)`,
          (value) => !value || value <= 10000000
        );
      }
    }

    if (
      type === "text" ||
      type === "textarea" ||
      type === "email" ||
      type === "password"
    ) {
      if (minLength !== undefined)
        rule = rule.min(
          minLength,
          `${field.label} must be at least ${minLength} characters`
        );
      if (maxLength !== undefined)
        rule = rule.max(
          maxLength,
          `${field.label} must be at most ${maxLength} characters`
        );
    }

    if (pattern) {
      rule = rule.matches(pattern.regex, pattern.message || "Invalid format");
    }

    if (customValidation) {
      rule = customValidation(rule);
    }

    if (field.validation) {
      rule = field.validation(rule);
    }

    schema[name] = rule;
  });
  return Yup.object().shape(schema);
};

const CommonForm = ({
  fields = [],
  initialData = {},
  onSubmit = () => {},
  submitButtonText = "Submit",
  className = "",
  children,
  showSubmitButton = true,
  showCancelButton = true,
  onCancel,
  onDraft = () => {},
  showDraftButton = false,
  draftButtonText = "Save Draft",
  cancelButtonText = "Cancel",
  columnsPerRow, // Keep for backward compatibility
  rowClassName = "",
  fieldClassName = "",
  gap = "gap-4",
  onValuesChange,
}) => {
  // Memoize the initial values to prevent unnecessary recalculations
  const initialValues = useMemo(() => {
    return buildInitialValues(fields, initialData);
  }, [fields, initialData]);

  const validationSchema = useMemo(
    () => buildValidationSchema(fields),
    [fields]
  );

  // Group fields into rows based on their colSpan values or columnsPerRow
  // Each row can have a maximum of 12 columns
  // Fields are automatically arranged to fit within the 12-column limit
  const groupFieldsIntoRows = useCallback(
    (fields) => {
      // If any field has colSpan defined, use the new dynamic system
      const hasColSpan = fields.some((field) => field.colSpan !== undefined);

      if (hasColSpan) {
        // New dynamic colSpan system
        const rows = [];
        let currentRow = [];
        let currentRowSpan = 0;

        fields.forEach((field) => {
          const fieldSpan = field.colSpan || 12; // Default to full width if not specified

          // If adding this field would exceed 12 columns, start a new row
          if (currentRowSpan + fieldSpan > 12) {
            if (currentRow.length > 0) {
              rows.push(currentRow);
            }
            currentRow = [field];
            currentRowSpan = fieldSpan;
          } else {
            currentRow.push(field);
            currentRowSpan += fieldSpan;
          }
        });

        // Add the last row if it has fields
        if (currentRow.length > 0) {
          rows.push(currentRow);
        }

        return rows;
      } else {
        // Legacy columnsPerRow system for backward compatibility
        const defaultColumnsPerRow = columnsPerRow || 1;
        const rows = [];
        for (let i = 0; i < fields.length; i += defaultColumnsPerRow) {
          rows.push(fields.slice(i, i + defaultColumnsPerRow));
        }
        return rows;
      }
    },
    [columnsPerRow]
  );

  const getColumnClasses = useCallback((columnsPerRow) => {
    const columnMap = {
      1: "md:col-span-12 col-span-1",
      2: "md:col-span-6 col-span-1",
      3: "md:col-span-4 col-span-1",
      4: "md:col-span-3 col-span-1",
      6: "md:col-span-2 col-span-1",
      12: "md:col-span-1 col-span-1",
    };
    return columnMap[columnsPerRow] || "md:col-span-12 col-span-1";
  }, []);

  const fieldRows = useMemo(
    () => groupFieldsIntoRows(fields),
    [groupFieldsIntoRows, fields]
  );

  // Use a ref to track previous values and defer onValuesChange
  const valuesRef = useRef(null);
  const onValuesChangeRef = useRef(onValuesChange);

  // Update ref when onValuesChange changes
  useEffect(() => {
    onValuesChangeRef.current = onValuesChange;
  }, [onValuesChange]);

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({
        values,
        touched,
        errors,
        handleBlur,
        setFieldValue,
        isSubmitting,
        setSubmitting,
        setTouched,
        setErrors,
        resetForm,
      }) => {
        // Defer onValuesChange to avoid setState during render
        // Only call if values have actually changed
        const valuesString = JSON.stringify(values);
        const prevValuesString = valuesRef.current ? JSON.stringify(valuesRef.current) : null;
        
        if (valuesString !== prevValuesString && typeof onValuesChangeRef.current === "function") {
          valuesRef.current = values;
          // Use queueMicrotask to defer the state update until after render
          queueMicrotask(() => {
            onValuesChangeRef.current(values, setFieldValue);
          });
        }
        
        return (
          <Form className={`flex flex-col gap-4 ${className}`}>
            {fieldRows.map((rowFields, rowIndex) => (
              <div
                key={`row-${rowIndex}`}
                className={`grid grid-cols-12 ${gap} ${rowClassName}`}
              >
                {rowFields.map((field) => {
                  const {
                    name,
                    type,
                    label,
                    placeholder,
                    required,
                    options = [],
                    disabled,
                    setSubmitting,
                    setTouched,
                    setErrors,
                    className: individualFieldClassName,
                    colSpan,
                    showIf,
                    hidden,
                    ...fieldProps
                  } = field;

                  if (hidden || type === "hidden") {
                    return null;
                  }

                  if (typeof showIf === "function" && !showIf(values)) {
                    return null;
                  }

                  {
                    /* const finalColumnClass =
                    type === "textarea"
                      ? "col-span-12"
                      : colSpan
                      ? `col-span-${colSpan}`
                      : getColumnClasses(columnsPerRow || 1); */
                  }

                  const finalColumnClass =
                    type === "textarea"
                      ? "col-span-12"
                      : colSpan
                      ? `col-span-12 md:col-span-${colSpan}`
                      : getColumnClasses(columnsPerRow || 1);

                  const getSelectValue = (fieldValue, options, multiple) => {
                    if (!options || options.length === 0) {
                      return multiple ? [] : null;
                    }

                    if (multiple) {
                      return Array.isArray(fieldValue)
                        ? options.filter((option) =>
                            fieldValue.includes(option.value)
                          )
                        : [];
                    } else {
                      // Check for null/undefined specifically, not falsy values (0 is valid)
                      return fieldValue != null
                        ? options.find((o) => o.value === fieldValue) || null
                        : null;
                    }
                  };

                  const fieldValue =
                    type === "select"
                      ? getSelectValue(
                          values[name],
                          options,
                          field.multiple || field.isMulti
                        )
                      : values[name];

                  const fieldComponentProps = {
                    name,
                    label,
                    value: fieldValue,
                    onBlur: handleBlur,
                    error: touched[name] && errors[name],
                    required,
                    placeholder,
                    disabled,
                    className: individualFieldClassName || fieldClassName,
                    ...Object.fromEntries(
                      Object.entries(fieldProps).filter(
                        ([key]) =>
                          ![
                            "name",
                            "label",
                            "value",
                            "onBlur",
                            "error",
                            "required",
                            "placeholder",
                            "disabled",
                            "className",
                          ].includes(key)
                      )
                    ),
                  };

                  const handleChange = (val, actionMeta) => {
                    let finalValue;

                    if (type === "checkbox") {
                      finalValue =
                        val?.target?.checked ??
                        (typeof val === "boolean" ? val : Boolean(val));
                    } else if (type === "select") {
                      finalValue =
                        field.multiple || field.isMulti
                          ? Array.isArray(val)
                            ? val.map((item) => item.value)
                            : []
                          : val?.value != null
                          ? val.value
                          : "";
                    } else if (type === "date") {
                      // Ensure date is a valid string format (YYYY-MM-DD) or empty string
                      finalValue = val && typeof val === "string" && val.trim() ? val.trim() : "";
                    } else if (type === "file") {
                      finalValue = Array.isArray(val) ? val : [];
                    } else if (type === "number") {
                      finalValue = val?.target?.value ?? "";
                    } else {
                      finalValue = val?.target?.value ?? val ?? "";
                    }

                    setFieldValue(name, finalValue, false); // Set value without validation

                    if (typeof field.onChange === "function") {
                      field.onChange(val, actionMeta, setFieldValue);
                    }
                    
                    // Trigger validation after a microtask to ensure value is set
                    if (type === "date" && finalValue) {
                      queueMicrotask(() => {
                        setFieldValue(name, finalValue, true); // Re-validate
                      });
                    }
                  };

                  let component;
                  switch (type) {
                    case "textarea":
                      component = (
                        <DefaultTextarea
                          {...fieldComponentProps}
                          onChange={handleChange}
                        />
                      );
                      break;
                    case "file":
                      {
                        // Support avatar-style file inputs (e.g., logo with hover edit icon)
                        const fieldFiles = values[name] || [];
                        const firstFile =
                          Array.isArray(fieldFiles) && fieldFiles.length
                            ? fieldFiles[0]
                            : null;
                        const imageUrl =
                          firstFile?.uploadUrl ||
                          firstFile?.path ||
                          firstFile?.localpreview ||
                          "";

                        if (field.avatar) {
                          const sizeClass =
                            field.avatarSizeClass || "w-20 h-20";
                          const roundedClass =
                            field.avatarRoundedClass || "rounded-full";

                          const AvatarComponent = (
                            <div className="inline-flex items-center justify-center">
                              <div
                                className={`relative group ${sizeClass} ${roundedClass} overflow-hidden bg-gray-100 border border-gray-200 flex items-center justify-center`}
                              >
                                {imageUrl ? (
                                  // Existing / uploaded logo preview
                                  <img
                                    src={imageUrl}
                                    alt={label || name}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  // Placeholder when no logo is present
                                  <span className="text-[10px] text-gray-400 text-center px-2">
                                    {field.placeholderAvatarText ||
                                      "Upload Logo"}
                                  </span>
                                )}

                                {/* Hover edit overlay */}
                                <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                  <FiEdit2 className="text-white text-xl" />
                                </div>
                              </div>
                            </div>
                          );

                          component = (
                            <div className="flex flex-col gap-2">
                              {label && (
                                <label className="text-theme font-medium text-sm">
                                  {label}
                                  {required && (
                                    <span className="text-red-600">*</span>
                                  )}
                                </label>
                              )}

                              <ReduxFileUploader
                                label=""
                                accept={field.accept || []}
                                limit={field.limit || 1}
                                folder={field.folder || ""}
                                useMasterApi={field.useMasterApi || false}
                                onChange={(files) =>
                                  setFieldValue(name, files || [])
                                }
                                value={fieldFiles}
                                multiple={false}
                                component={AvatarComponent}
                              />

                              {field.info && (
                                <p className="text-xs text-textLight mt-1">
                                  {field.info}
                                </p>
                              )}
                            </div>
                          );
                        } else {
                          component = (
                            <div className="flex flex-col gap-2">
                              <ReduxFileUploader
                                label={
                                  field.uploadLabel || "Upload Document"
                                }
                                accept={field.accept || []}
                                limit={field.limit || 5}
                                folder={field.folder || ""}
                                useMasterApi={field.useMasterApi || false}
                                onChange={(files) =>
                                  setFieldValue(name, files || [])
                                }
                                value={values[name] || []}
                              />
                            </div>
                          );
                        }
                      }
                      break;

                    case "select":
                      component = (
                        <DefaultSearchSelectInput
                          {...fieldComponentProps}
                          isDisabled={disabled}
                          addmoreClick={field.addmoreClick}
                          onAddMore={field.onAddMore}
                          onRefresh={field.onRefresh}
                          options={options}
                          onChange={handleChange}
                          isMulti={field.multiple || field.isMulti}
                        />
                      );
                      break;

                    case "radio":
                      component = (
                        <RadioInput
                          {...fieldComponentProps}
                          options={field.options || field.options || []}
                          onChange={handleChange}
                          required={required}
                          horizontal
                        />
                      );
                      break;

                    case "date":
                      // Handle function-based minDate and maxDate
                      const processedFieldProps = { ...fieldComponentProps };

                      // If minDate is a function, call it with current form values
                      if (typeof field.minDate === "function") {
                        processedFieldProps.minDate = field.minDate(values);
                      }

                      // If maxDate is a function, call it with current form values
                      if (typeof field.maxDate === "function") {
                        processedFieldProps.maxDate = field.maxDate(values);
                      }

                      component = (
                        <DefaultDatePicker
                          {...processedFieldProps}
                          onChange={handleChange}
                        />
                      );
                      break;
                    case "checkbox":
                      component = (
                        <DefaultCheckbox
                          type="checkbox"
                          {...fieldComponentProps}
                          onChange={handleChange}
                        />
                      );
                      break;

                    case "section_header":
                      component = (
                        <div
                          className={`w-full ${individualFieldClassName || ""}`}
                        >
                          <h3 className="font-medium text-xl py-4 border-b text-theme">
                            {label}
                          </h3>
                        </div>
                      );
                      break;
                    case "dynamicSearchSelect":
                      component = (
                        <DynamicSearchSelectInput
                          {...fieldComponentProps}
                          options={field.options || []}
                          onChange={(val) => setFieldValue(name, val)}
                          isMulti={field.multiple || field.isMulti}
                        />
                      );
                      break;
                    case "contact":
                      component = (
                        <div className="w-full">
                          <label className="block mb-1 text-sm font-medium text-theme">
                            {label}
                            {required && (
                              <span className="text-red-500">*</span>
                            )}
                          </label>

                          <div className="flex gap-2">
                            <select
                              className={`px-2 py-1 border rounded-md text-sm w-[100px] ${
                                touched[name] && errors[name]
                                  ? "bg-red-100 border-red-200"
                                  : "border-borderColor bg-light"
                              }`}
                              value={values[field.countryCodeName] || ""}
                              onChange={(e) =>
                                setFieldValue(
                                  field.countryCodeName,
                                  e.target.value
                                )
                              }
                            >
                              <option value="+91">🇮🇳 +91</option>
                              <option value="+1">🇺🇸 +1</option>
                              <option value="+44">🇬🇧 +44</option>
                              <option value="+1">🇨🇦 +1</option>
                              <option value="+61">🇦🇺 +61</option>
                              <option value="+49">🇩🇪 +49</option>
                              <option value="+33">🇫🇷 +33</option>
                              <option value="+81">🇯🇵 +81</option>
                              <option value="+86">🇨🇳 +86</option>
                              <option value="+55">🇧🇷 +55</option>
                              <option value="other">🌍 Other</option>
                            </select>

                            <DefaultInput
                              type="number"
                              name={name}
                              value={values[name]}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              placeholder={placeholder || "Enter phone number"}
                              classnames={`flex-1 ${
                                touched[name] && errors[name]
                                  ? "!bg-red-100 border !border-red-200 !rounded-md"
                                  : ""
                              }`}
                            />
                          </div>

                          {touched[name] && errors[name] && (
                            <div className="text-red-500 text-xs mt-1">
                              {errors[name]}
                            </div>
                          )}
                        </div>
                      );
                      break;

                    default:
                      component = (
                        <DefaultInput
                          type={type}
                          {...fieldComponentProps}
                          onChange={handleChange}
                        />
                      );
                  }

                  return (
                    <div key={name} className={finalColumnClass}>
                      {component}
                    </div>
                  );
                })}
              </div>
            ))}

            {children}

            <div className="flex gap-2">
              {showSubmitButton && (
                <DefaultButton
                  name={submitButtonText}
                  type="submit"
                  loading={isSubmitting}
                />
              )}
              {showCancelButton && onCancel && (
                <DefaultButton
                  variant="outline"
                  name={cancelButtonText}
                  onClick={onCancel}
                />
              )}
              {showDraftButton && onDraft && (
                <DefaultButton
                  variant="outline"
                  name={draftButtonText}
                  loading={isSubmitting}
                  disabled={isSubmitting}
                  onClick={() => {
                    // This is the fix — pass the full formik bag
                    onDraft(values, {
                      setSubmitting,
                      resetForm,
                      setTouched,
                      setErrors,
                      setFieldValue,
                      values,
                      errors, // optional: helpful for custom logic
                    });
                  }}
                />
              )}
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default CommonForm;
